import { createApp } from 'vue';
import DesignSystem, { defaultLocale, supportedLocales } from 'shipbob_design_system';
import axios from 'axios';
import VueAxios from 'vue-axios';
import { ShipBobTelemetry } from '@shipbob/telemetry';
import MFERuntime from 'shipbob_mfe_runtime';
import packages from '../package.json'

import { initializeRouter } from '@/router';
import { ENV } from '@/env';
import App from '@/components/App.vue';
import { getAuthService, getDelegationToken } from '@/auth';
import { createStoreForEnvironment } from '@/store/createStore';
import SimpleInjector, { register } from './services/shared/simple-injector';
import FulfillmentCustomizationService from './services/fulfillmentCustomization.service';
import PackingSlipService from './services/packingSlip.service';
import {DocumentConfigurationService, HttpClient} from './services';
import { FrontendAppSettingsService } from './services/FrontendAppSettings.service';

import '@/style/core.scss';

/**
 * Mounts the Vue application to a host MFE or mounts to dev root DOM element
 * @param {HTMLElement} $element - the element to mount the app to
 * @param {Object} hostStore - the host store passed down from the host of MFE
 * @param {String} hostPath - the host path to mount the app to
 * @param {String} hostRouter - the host router which can be used to navigate in the host of MFE
 * @param {Object} parentAuthService - the auth service from parent application
 * @param {Object} standAlone - whether the app is being mounted in standalone mode, 
 * standlone mode is used for local development
 * @param {Object} hostLocale - the locale of the host application, should be used to set the language of the app via DS
 * @param {Object} host - name of the host applicaiton, `none` when in standalone mode
 * @param {Object} isTelemetryEnabled - whether telemetry is enabled or not
 */
export const mount = async (args) => {
    const {
        $element = null,
        hostStore = null,
        hostPath = ENV.MFE_ROOT_ROUTE,
        hostRouter = null,
        parentAuthService = null,
        standAlone = false,
        hostLocale = defaultLocale,
        host = 'none',
        isTelemetryEnabled = ENV.APPINSIGHTS_ENABLED,
    } = args
    if (!$element) {
        console.error(`Element is required to mount app, provided ${$element}`);
        return
    }
    // set up auth and make sure to use latest token when a call
    // is being made
    const authService = await getAuthService(parentAuthService);

    const delegatedToken = getDelegationToken();
    if (ENV.ENABLE_TOKEN_DELEGATION && delegatedToken) {
      await authService.delegateToken(delegatedToken);
    }

    axios.interceptors.request.use(async request => {
        const access_token = await authService.getToken();
        request.headers.common.Authorization = `Bearer ${access_token}`;

        return request;
    });

    // create app
    const app = createApp(App, { hostPath });

    // use MFE Runtime
    app.use(MFERuntime, {
        ...args,
    })

    // set up $http in each component
    app.use(VueAxios, axios);

    // install vue-router etc
    const router = initializeRouter({ hostRouter });
    app.use(router);

    // application insights
    const claims = await authService.getTokenClaims();

    let logger = null;
    if (isTelemetryEnabled && ENV.APPINSIGHTS_CONNECTIONSTRING) {
        logger = new ShipBobTelemetry({
            connectionString: ENV.APPINSIGHTS_CONNECTIONSTRING,
            tags: {
                'ai.cloud.role': ENV.CLIENT_ID,
                'ai.user.id': claims?.shipbob_user_id,
                'ai.application.ver': packages.version,
            },
            customData: {
                host,
                userImpersonationId: claims?.shipbob_impersonating_user_id,
                userRole: claims?.shipbob_role,
            },
            config: {
                loggingLevelConsole: 2,
                loggingLevelTelemetry: 2,
            },
            router,
        });
    }

    if (logger) app.provide('logger', logger);

    /**
     * NOTE:
     * If you need to specify the language setting for headers, set it here.
     * The following is an example for axios and it should be configured on the server too,
     * that's how API would know which language to return the response in.
     *
     * axios.defaults.headers.common['Accept-Language'] = hostLocale
     */
    if (standAlone) {
        document.querySelector('html').setAttribute('lang', hostLocale);
    }

    // set up design system with i18n
    const selectedLocale = supportedLocales.includes(hostLocale) ? hostLocale : defaultLocale
    let appMessages = null;

    try {
        appMessages = (await import(`./locales/${selectedLocale}.json`)).default
    } catch (e) {
        appMessages = (await import(`./locales/${defaultLocale}.json`)).default
        console.error('could not load selected locale file: ', selectedLocale)
    }

    app.use(DesignSystem, {
        ...args,
        lang: hostLocale,
        appMessages,
        version: {
            SbButton: 'next',
            SbPage: 'next',
        },
    });

    // set up store
    const store = await createStoreForEnvironment();
    app.use(store);

    /** 
     * For production, you can read values from the $hostStore.
     * For development, those values should be added to the native store
     * */
    if (hostStore) {
        app.provide('$hostStore', hostStore);
    }

    /** This should only be used to navigate in the host MFE */
    if (hostRouter) {
        app.provide('$hostRouter', hostRouter);
    }

    app.provide('host', host);

    const injector = new SimpleInjector(app);
    injector.registerServices({
        store,
        registrations: [
            register(FulfillmentCustomizationService).as('$fulfillmentCustomizationService'),
            register(PackingSlipService).as('$packingSlipService'),
        ],
    });

    app.provide('$documentConfigurationService', new DocumentConfigurationService(new HttpClient()));
    app.provide('$frontendAppSettingsService', new FrontendAppSettingsService(new HttpClient()));

    /**
     * Consult platform-ui team if you need to handle mounting differently or if something goes awry.
     * Please make sure to keep the standAlone parameter or mounting will not work.
     * The build will fail if it cannot find usage of app.mount.
     */

let elementToMount = $element;
let locationForStyles = document.head;
const styles = []
const dsStyles = window?.sbStyles?.shipbobDesignSystem;
const mfeStyles = window?.sbStyles?.[ENV.ROOT_ELEMENT_CONTAINER];

if (dsStyles) {
  styles.push(...dsStyles);
}

if (!standAlone) {
  const body = document.createElement('body');
  elementToMount = document.createElement('div');
  elementToMount.setAttribute('id', ENV.ROOT_ELEMENT_CONTAINER);
  $element.attachShadow({ mode: 'open' });
  locationForStyles = $element.shadowRoot;
  body.appendChild(elementToMount);
  $element.shadowRoot.appendChild(body);
  
  if (mfeStyles) {
    styles.push(...mfeStyles);
  }
}

styles.forEach((style, index) => {
  const clone = style.cloneNode(true);
  
  if (styles.length - 1 === index) {
    clone.onload = () => {
      app.mount(elementToMount);
    };
  }
  
  locationForStyles.append(clone);
})

if (!(styles && styles.length)) {
  app.mount(elementToMount);
}

    return app.unmount;
};

const devEl = globalThis.document.querySelector(ENV.DEV_ROOT_ELEMENT_SELECTOR);
const { pathname, search } = window.location;

if (devEl) {
    const queryParams = new URLSearchParams(search);
    let host = queryParams.get('host') || 'none';
    mount({
        $element: devEl,
        hostPath: (pathname !== '/' && pathname !== ENV.MFE_ROOT_ROUTE) ? pathname : ENV.MFE_ROOT_ROUTE,
        host,
        standAlone: true,
    });
}
